import { create } from 'zustand';
import { useAuthStore } from './authStore';
import { getDeviceLocation } from '../utils/geolocation';

interface WeatherLocation {
  name: string;
  region: string;
  country: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
}

interface AirQuality {
  pm2_5: number;
  pm10: number;
  o3: number;
  no2: number;
  so2: number;
  co: number;
  usEpaIndex: number;
  gbDefraIndex: number;
}

interface WeatherAlert {
  headline: string;
  event: string;
  severity: string;
  urgency: string;
  areas: string;
  category: string;
  certainty: string;
  effective: string;
  expires: string;
  description: string;
  instruction: string;
}

interface WeatherHourly {
  time: string;
  timeEpoch: number;
  temperature: {
    fahrenheit: number;
    celsius: number;
  };
  condition: string;
  icon: string;
  humidity: number;
  wind: {
    speed: number;
    direction: string;
  };
  chanceOfRain: number;
  chanceOfSnow: number;
}

interface WeatherCurrent {
  temperature: {
    fahrenheit: number;
    celsius: number;
  };
  condition: string;
  icon: string;
  humidity: number;
  wind: {
    speed: number;
    direction: string;
  };
  airQuality: AirQuality | null;
}

interface WeatherForecastDay {
  date: string;
  high: {
    fahrenheit: number;
    celsius: number;
  };
  low: {
    fahrenheit: number;
    celsius: number;
  };
  condition: string;
  icon: string;
  hourly: WeatherHourly[];
}

interface WeatherMaps {
  radar: string;
  satellite: string;
  temperature: string;
  precipitation: string;
  pressure?: string;
  wind?: string;
}

interface WeatherData {
  location: WeatherLocation;
  current: WeatherCurrent;
  alerts: WeatherAlert[];
  forecast: WeatherForecastDay[];
  maps: WeatherMaps;
}

interface WeatherState {
  weather: WeatherData | null;
  isLoading: boolean;
  error: string | null;
  lastFetch: number | null;
  fetchWeather: (location?: string, forceRefresh?: boolean) => Promise<void>;
  clearError: () => void;
}

// In development, always use proxy to avoid CORS issues when accessing from network devices
// In production, use configured endpoint or default to production API
import { getApiUrl, getApiKey } from '../utils/apiConfig';

// Get API configuration (throws if not configured)
const API_URL = getApiUrl();
const API_KEY = getApiKey();

// Cache duration: 10 minutes (600,000 ms)
const CACHE_DURATION = 10 * 60 * 1000;

// Helper to get headers with auth token and API key
const getHeaders = (): HeadersInit => {
  const { idToken } = useAuthStore.getState();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (API_KEY) {
    headers['X-Api-Key'] = API_KEY;
  }
  if (idToken) {
    headers['Authorization'] = `Bearer ${idToken}`;
  }
  return headers;
};

export const useWeatherStore = create<WeatherState>((set, get) => ({
  weather: null,
  isLoading: false,
  error: null,
  lastFetch: null,

  fetchWeather: async (location: string = 'family', forceRefresh: boolean = false) => {
    const state = get();
    
    // Check cache - if data exists and is less than CACHE_DURATION old, don't fetch
    // Unless forceRefresh is true
    if (!forceRefresh && state.weather && state.lastFetch) {
      const now = Date.now();
      const cacheAge = now - state.lastFetch;
      if (cacheAge < CACHE_DURATION) {
        console.log('[weatherStore] Using cached weather data');
        return;
      }
    }

    set({ isLoading: true, error: null });
    
    // Handle device location - get coordinates first
    let locationQuery = location;
    if (location === 'device') {
      const deviceCoords = await getDeviceLocation();
      if (deviceCoords) {
        locationQuery = deviceCoords;
        console.log('[weatherStore] Using device location:', deviceCoords);
      } else {
        // Fallback to family location if geolocation fails
        console.warn('[weatherStore] Device location unavailable, falling back to family location');
        locationQuery = 'family';
      }
    }
    
    // Retry configuration for frontend
    const MAX_RETRIES = 2;
    const RETRY_DELAY_MS = 2000; // 2 seconds
    
    let lastError: any = null;
    
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const url = `${API_URL}/weather?location=${encodeURIComponent(locationQuery)}`;

        const response = await fetch(url, {
          method: 'GET',
          headers: getHeaders(),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Failed to fetch weather' }));
          const errorMessage = errorData.message || `HTTP ${response.status}`;
          
          // Don't retry on client errors (4xx) except 429 (rate limit)
          if (response.status >= 400 && response.status < 500 && response.status !== 429) {
            throw new Error(errorMessage);
          }
          
          // For 429 or 5xx errors, retry
          if (response.status === 429 || response.status >= 500) {
            lastError = new Error(errorMessage);
            if (attempt < MAX_RETRIES - 1) {
              console.log(`[weatherStore] Retrying weather fetch in ${RETRY_DELAY_MS}ms (attempt ${attempt + 1}/${MAX_RETRIES})`);
              await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
              continue;
            }
            throw lastError;
          }
          
          throw new Error(errorMessage);
        }

        const data = await response.json();
        set({
          weather: data,
          isLoading: false,
          error: null,
          lastFetch: Date.now(),
        });
        return; // Success, exit retry loop
      } catch (error: any) {
        lastError = error;
        console.error(`[weatherStore] Error fetching weather (attempt ${attempt + 1}/${MAX_RETRIES}):`, error);
        
        // If this is the last attempt, set error state
        if (attempt === MAX_RETRIES - 1) {
          set({
            error: error.message || 'Failed to fetch weather. Please try again in a moment.',
            isLoading: false,
          });
          return;
        }
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
      }
    }
  },

  clearError: () => set({ error: null }),
}));
